import { LayoutForm } from '@xpeng/xdragon-ass-components';
import { Button, Form, message } from 'antd';
import React, { useMemo, useState, useCallback } from 'react';
import { useRequest } from 'ahooks';
import api from '@/api';
import { getManualList } from '@/api/sip/bookmap';
import { useTranslation } from '@/hooks/useTranslation';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/models/store';
import styles from './index.module.less';

const SearchInline = () => {
  const $ = useTranslation();
  const [form] = Form.useForm();
  const [values, setValues] = useState<any>({});
  const [type, setType] = useState(1);

  const region = useSelector<RootState, string>(
    state => state.LocaleModel.countryCode,
    shallowEqual
  );
  const lang = useSelector<RootState, string>(state => state.LocaleModel.serverLang, shallowEqual);
  const isGlobal = useSelector<RootState>(state => state.LocaleModel.isGlobal, shallowEqual);

  const { data: brandOpt } = useRequest(
    async () => {
      if (!region) return [];
      let res: any = {};
      let result: any[] = [];
      // 国际查车系车型
      if (isGlobal) {
        res = await api.sip.bookmap.getSeriesModelList({
          brandCode: 'xp',
          region
        });
        result = (res.data.seriesList ?? []).map(series => {
          const modelList =
            series.modelList?.map(model => {
              const str = `${series.seriesName}/${model.modelName}`;
              return {
                value: model.modelCode,
                label: model.modelName,
                showLabel: str
              };
            }) ?? [];
          return {
            value: series.seriesCode,
            label: series.seriesName,
            selectable: false,
            children: modelList
          };
        });
      } else {
        // 国内查车系
        res = await api.sip.bookmap.getSeriesList({
          lang,
          region
        });
        result = (res.data.seriesList ?? []).map(v => ({
          label: v.seriesName,
          value: v.modelCode
        }));
      }

      if (res.code !== 200) {
        message.error(res.msg);
        return [];
      }
      return result;
    },
    { manual: false, refreshDeps: [region] }
  );

  const formItems: any = useMemo(() => {
    const items: any[] = [
      {
        name: 'type',
        label: '',
        type: 'radio',
        options: [
          {
            label: $('VIN码查找'),
            value: 1
          },
          {
            label: isGlobal ? $('车型查找') : $('车系查找'),
            value: 2
          }
        ],
        initialValue: type,
        props: {
          optionType: 'button'
        }
      },
      {
        name: 'vin',
        label: $('VIN码'),
        hidden: type !== 1,
        type: 'input',
        rules: [
          {
            pattern: /^[a-zA-Z0-9]+$/,
            message: $('请输入英文或数字')
          }
        ],
        props: {
          maxLength: 17
        }
      },
      {
        name: 'modelCode',
        label: $('车系'), // 国内
        hidden: type !== 2 || isGlobal,
        type: 'select',
        useOptionWhenSearch: false,
        props: {
          showSearch: true,
          filterOption: (input, option) =>
            (option!.label as string)?.toLowerCase().includes(input.toLowerCase()),
          allowClear: true
        },
        options: brandOpt ?? []
      },
      {
        name: 'modelCode',
        label: $('品牌车系车型'), // 国际
        hidden: type !== 2 || !isGlobal,
        type: 'cascader',
        props: {
          showSearch: {
            filter: (inputValue, path) => {
              return path.some(option => option.showLabel?.includes(inputValue));
            }
          },
          popupClassName: styles['search-car-cascader'],
          displayRender: (_, selectedOptions) => {
            if (!selectedOptions.filter(v => !!v).length) return '';
            return selectedOptions[selectedOptions.length - 1].showLabel;
          },
          allowClear: true,
          multiple: false,
          expandTrigger: 'hover',
          showCheckedStrategy: 'SHOW_CHILD'
        },
        options: brandOpt ?? []
      }
    ];

    return items;
  }, [type, brandOpt, $, isGlobal]);

  const { run: onFinish, loading: submitLoading } = useRequest(
    async formValues => {
      const { vin, modelCode } = formValues;

      const params: any = {
        lang,
        region
      };
      if (formValues.type === 1) {
        params.vin = vin;
      } else {
        if (isGlobal) {
          if (modelCode && modelCode.length) {
            params.modelCode = modelCode[modelCode.length - 1];
          }
        } else params.modelCode = modelCode;
      }

      const open = window.xDragonBridge.openNewTab;

      if (vin || modelCode) {
        const res = await getManualList(params);
        if (res.code !== 200) {
          message.error(res.msg);
          return;
        }
        if (formValues.type === 1) {
          open({
            path: `/afs-sip/repair-info-search/vin/${vin}/${
              res.data.modelCode
            }?should_refresh=${Date.now()}`
          });
        } else {
          open({
            path: `/afs-sip/repair-info-search/car-model/${
              params.modelCode
            }?should_refresh=${Date.now()}`
          });
        }
      }
    },
    { manual: true }
  );

  const onValuesChange = useCallback((_, allFields) => {
    setValues(allFields);
  }, []);

  const disable = useMemo(() => {
    return type === 1 ? !form.getFieldValue('vin') : !form.getFieldValue('modelCode');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, type]);

  return (
    <>
      <LayoutForm
        formItems={formItems}
        form={form}
        onFieldsChange={value => {
          if (value && value.length && value[0].name && value[0].name[0] === 'type') {
            setType(value[0].value);
          }
        }}
        mode={'form'}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        span={8}
        style={{
          padding: '12px',
          paddingBottom: '0',
          background: '#ffffff',
          borderRadius: '6px',
          width: '100%',
          marginBottom: '12px'
        }}
      >
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type='primary' htmlType='submit' loading={submitLoading} disabled={disable}>
            {$('查询')}
          </Button>
        </div>
      </LayoutForm>
    </>
  );
};
const Search = React.memo(SearchInline);
export default Search;
